"use client";

import type { ChartSpec, ChartPoint } from "@/lib/exams/ielts/stimulus";

const PALETTE = ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#0ea5e9", "#8b5cf6"];

// Round an axis maximum up to a clean tick value, never zero.
export const niceMax = (raw: number): number => {
  if (raw <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(raw)));
  const steps = [1, 2, 2.5, 5, 10];
  for (const s of steps) {
    const candidate = s * pow;
    if (candidate >= raw) return candidate;
  }
  return 10 * pow;
};

// Evenly spaced y-axis tick values from 0 up to niceMax(rawMax). 5 intervals
// keep the labels whole for the maxima niceMax emits (10→2, 20→4, 25→5, 50→10),
// so the learner can read figures off the scale — the data citation IELTS
// Task Achievement rewards. Round trims binary-float dust (e.g. 0.1*3).
export const axisTicks = (rawMax: number, intervals = 5): number[] => {
  const max = niceMax(rawMax);
  return Array.from(
    { length: intervals + 1 },
    (_, i) => Math.round((max / intervals) * i * 1000) / 1000,
  );
};

export interface BarRect {
  x: number;
  y: number;
  width: number;
  height: number;
  label: string;
}

// Lay out vertical bars in a [plotW × plotH] box (origin at top-left of the plot).
export const barLayout = (
  points: ChartPoint[],
  plotW: number,
  plotH: number,
  gap: number,
): BarRect[] => {
  const max = niceMax(Math.max(...points.map((p) => p.value), 0));
  const slot = plotW / points.length;
  const width = Math.max(1, slot - gap);
  return points.map((p, i) => {
    const height = (p.value / max) * plotH;
    return { x: i * slot + gap / 2, y: plotH - height, width, height, label: p.label };
  });
};

export interface PieSlice {
  label: string;
  angle: number;
  startAngle: number;
  color: string;
}

export const pieSlices = (points: ChartPoint[]): PieSlice[] => {
  const total = points.reduce((s, p) => s + p.value, 0) || 1;
  let start = 0;
  return points.map((p, i) => {
    const angle = (p.value / total) * Math.PI * 2;
    const slice = { label: p.label, angle, startAngle: start, color: PALETTE[i % PALETTE.length] };
    start += angle;
    return slice;
  });
};

// SVG path for one line series across a [plotW × plotH] box.
export const linePath = (
  points: ChartPoint[],
  plotW: number,
  plotH: number,
  axisMax: number,
): string => {
  const max = axisMax > 0 ? axisMax : niceMax(Math.max(...points.map((p) => p.value), 0));
  const step = points.length > 1 ? plotW / (points.length - 1) : 0;
  return points
    .map((p, i) => {
      const x = i * step;
      const y = plotH - (p.value / max) * plotH;
      return `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
};

const polar = (cx: number, cy: number, r: number, angle: number) => ({
  x: cx + r * Math.sin(angle),
  y: cy - r * Math.cos(angle),
});

const arcPath = (cx: number, cy: number, r: number, slice: PieSlice): string => {
  const a0 = slice.startAngle;
  const a1 = slice.startAngle + slice.angle;
  const p0 = polar(cx, cy, r, a0);
  const p1 = polar(cx, cy, r, a1);
  const large = slice.angle > Math.PI ? 1 : 0;
  return `M${cx},${cy} L${p0.x.toFixed(2)},${p0.y.toFixed(2)} A${r},${r} 0 ${large} 1 ${p1.x.toFixed(2)},${p1.y.toFixed(2)} Z`;
};

export const IeltsTask1Chart = ({ spec }: { spec: ChartSpec }) => {
  return (
    <figure className="rounded-xl border border-gray-200 bg-white p-4">
      <figcaption className="mb-3 text-center text-sm font-semibold text-gray-800">
        {spec.title}
      </figcaption>
      {spec.kind === "table" ? (
        <ChartTable spec={spec} />
      ) : spec.kind === "pie" ? (
        <PieChart spec={spec} />
      ) : spec.kind === "line" ? (
        <LineChart spec={spec} />
      ) : (
        <BarChart spec={spec} />
      )}
    </figure>
  );
};

const W = 320,
  H = 220,
  PAD_L = 36,
  PAD_B = 36,
  PAD_T = 16,
  PAD_R = 8;
const plotW = W - PAD_L - PAD_R;
const plotH = H - PAD_T - PAD_B;

const Axes = ({ unit, categoryAxis }: { unit: string; categoryAxis: string }) => (
  <>
    <line x1={PAD_L} y1={PAD_T} x2={PAD_L} y2={PAD_T + plotH} stroke="#9ca3af" strokeWidth={1} />
    <line
      x1={PAD_L}
      y1={PAD_T + plotH}
      x2={PAD_L + plotW}
      y2={PAD_T + plotH}
      stroke="#9ca3af"
      strokeWidth={1}
    />
    <text x={6} y={PAD_T - 6} className="fill-gray-400 text-[8px]">
      {unit}
    </text>
    <text x={PAD_L + plotW} y={H - 4} textAnchor="end" className="fill-gray-400 text-[8px]">
      {categoryAxis}
    </text>
  </>
);

// Numbered y-axis: faint gridlines + tick labels so the learner can read figures
// off the scale. Drawn before the data so bars/lines sit on top.
const GridY = ({ rawMax }: { rawMax: number }) => {
  const max = niceMax(rawMax);
  return (
    <>
      {axisTicks(rawMax).map((t, i) => {
        const y = PAD_T + plotH - (t / max) * plotH;
        return (
          <g key={i}>
            {t > 0 && (
              <line x1={PAD_L} y1={y} x2={PAD_L + plotW} y2={y} stroke="#eef0f4" strokeWidth={1} />
            )}
            <text x={PAD_L - 5} y={y + 3} textAnchor="end" className="fill-gray-400 text-[8px]">
              {t}
            </text>
          </g>
        );
      })}
    </>
  );
};

const BarChart = ({ spec }: { spec: ChartSpec }) => {
  const points = spec.series[0].points;
  const rawMax = Math.max(...points.map((p) => p.value), 0);
  const bars = barLayout(points, plotW, plotH, 12);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
      <GridY rawMax={rawMax} />
      <Axes unit={spec.unit} categoryAxis={spec.categoryAxis} />
      {bars.map((b, i) => (
        <g key={i}>
          <rect
            x={PAD_L + b.x}
            y={PAD_T + b.y}
            width={b.width}
            height={b.height}
            fill={PALETTE[0]}
            rx={2}
          />
          <text
            x={PAD_L + b.x + b.width / 2}
            y={H - PAD_B + 12}
            textAnchor="middle"
            className="fill-gray-500 text-[8px]"
          >
            {b.label}
          </text>
        </g>
      ))}
    </svg>
  );
};

const LineChart = ({ spec }: { spec: ChartSpec }) => {
  const rawMax = Math.max(...spec.series.flatMap((s) => s.points.map((p) => p.value)), 0);
  const axisMax = niceMax(rawMax);
  const labels = spec.series[0].points.map((p) => p.label);
  const step = labels.length > 1 ? plotW / (labels.length - 1) : 0;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
      <GridY rawMax={rawMax} />
      <Axes unit={spec.unit} categoryAxis={spec.categoryAxis} />
      {spec.series.map((s, si) => (
        <g key={si} transform={`translate(${PAD_L},${PAD_T})`}>
          <path
            d={linePath(s.points, plotW, plotH, axisMax)}
            fill="none"
            stroke={PALETTE[si % PALETTE.length]}
            strokeWidth={2}
          />
        </g>
      ))}
      {labels.map((label, i) => (
        <text
          key={i}
          x={PAD_L + i * step}
          y={H - PAD_B + 12}
          textAnchor="middle"
          className="fill-gray-500 text-[8px]"
        >
          {label}
        </text>
      ))}
      <Legend names={spec.series.map((s) => s.name)} />
    </svg>
  );
};

const PieChart = ({ spec }: { spec: ChartSpec }) => {
  const slices = pieSlices(spec.series[0].points);
  const cx = W / 2,
    cy = H / 2 - 6,
    r = Math.min(plotW, plotH) / 2 - 4;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
      {slices.map((s, i) => (
        <path key={i} d={arcPath(cx, cy, r, s)} fill={s.color} stroke="#fff" strokeWidth={1} />
      ))}
      <Legend names={spec.series[0].points.map((p) => `${p.label}: ${p.value}${spec.unit}`)} />
    </svg>
  );
};

const Legend = ({ names }: { names: string[] }) => (
  <g>
    {names.map((name, i) => (
      <g
        key={i}
        transform={`translate(${PAD_L + (i % 3) * 96}, ${H - 10 + Math.floor(i / 3) * 10})`}
      >
        <rect width={8} height={8} y={-7} fill={PALETTE[i % PALETTE.length]} rx={1} />
        <text x={11} className="fill-gray-500 text-[8px]">
          {name}
        </text>
      </g>
    ))}
  </g>
);

const ChartTable = ({ spec }: { spec: ChartSpec }) => (
  <table className="w-full border-collapse text-xs">
    <thead>
      <tr>
        <th className="border border-gray-200 px-2 py-1 text-left text-gray-600">
          {spec.categoryAxis}
        </th>
        {spec.series.map((s) => (
          <th key={s.name} className="border border-gray-200 px-2 py-1 text-right text-gray-600">
            {s.name} ({spec.unit})
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {spec.series[0].points.map((p, ri) => (
        <tr key={ri}>
          <td className="border border-gray-200 px-2 py-1 text-gray-700">{p.label}</td>
          {spec.series.map((s) => (
            <td key={s.name} className="border border-gray-200 px-2 py-1 text-right text-gray-800">
              {s.points[ri]?.value}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);
